import { AppLogEnvironment } from "@/types/app-log-types";

export function getAppLogEnvironment(): AppLogEnvironment {
  const vercelEnv =
    process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.VERCEL_ENV;

  if (vercelEnv === "production") return "production";
  if (vercelEnv === "preview") return "preview";
  if (process.env.NODE_ENV === "production") return "production";

  return "development";
}

export function shouldSendAppLogs() {
  const vercelEnv =
    process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.VERCEL_ENV;

  if (vercelEnv) return vercelEnv === "production";

  return process.env.NODE_ENV === "production";
}

/**
 * Per-page-view `page_view` app-logs. OFF unless explicitly switched on.
 *
 * To turn back on:
 *     NEXT_PUBLIC_APP_LOG_PAGE_VIEWS=true
 * in .env / .env.production, or as a Vercel project environment variable.
 * Any other value (or unset) leaves it off.
 *
 * Off by default because each page view POSTs to /api/app-logs on our own
 * domain — an uncached edge request plus a function invocation per view — and
 * Vercel Analytics already records page views. Turn it on temporarily when you
 * need the richer payload (referrer, viewport, device, navigation type) that
 * Analytics does not give you, then turn it off again.
 *
 * This is a BUILD-TIME switch, not a runtime one. NEXT_PUBLIC_ values are
 * inlined into the client bundle by the compiler, so flipping it on Vercel
 * requires a redeploy to take effect. That is also why the value must stay
 * constant per build — a value that changed every build would change the
 * content hash of every chunk that reads it and force returning visitors to
 * re-download them.
 *
 * `shouldSendAppLogs()` still applies on top of this: page views are never sent
 * outside production regardless of this flag.
 */
export function shouldSendPageViewLogs() {
  if (!shouldSendAppLogs()) return false;

  return process.env.NEXT_PUBLIC_APP_LOG_PAGE_VIEWS === "true";
}

/**
 * Fraction of sessions whose client errors get reported, 0..1.
 *
 *     NEXT_PUBLIC_APP_LOG_ERROR_SAMPLE_RATE=0.1     (default: 10%)
 *     NEXT_PUBLIC_APP_LOG_ERROR_SAMPLE_RATE=1       (report everything)
 *     NEXT_PUBLIC_APP_LOG_ERROR_SAMPLE_RATE=0       (report nothing)
 *
 * Sampling is per SESSION, not per event: a sampled-in session reports all of
 * its errors and a sampled-out session reports none. Per-event sampling would
 * give you scattered fragments of each error sequence, which is much harder to
 * debug from. Anything unparseable falls back to the 10% default rather than
 * silently reporting nothing.
 */
export function getAppLogErrorSampleRate(): number {
  const raw = process.env.NEXT_PUBLIC_APP_LOG_ERROR_SAMPLE_RATE;
  if (raw === undefined || raw === "") return 0.1;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 0.1;

  return Math.min(Math.max(parsed, 0), 1);
}

/**
 * POST browser app-logs straight to the Go logging API instead of proxying
 * them through our own /api/app-logs route.
 *
 *     NEXT_PUBLIC_APP_LOG_DIRECT=true
 *
 * OFF by default, deliberately. Requests to a third-party origin are not
 * Vercel Edge Requests at all, so this is the biggest available saving on the
 * logging path — but it only works if the Go /logs handler accepts a
 * cross-origin POST whose Content-Type is text/plain (see the transport notes
 * in app_logs.client.ts for why it has to be text/plain). That is a property
 * of a service this repo does not contain, so it cannot be verified from here.
 *
 * Enabling this without checking would lose client logs silently — the send is
 * fire-and-forget, so a rejected request looks exactly like a successful one.
 * Verify first, then flip. See edge.txt #2 for the check.
 */
export function shouldPostAppLogsDirect() {
  return process.env.NEXT_PUBLIC_APP_LOG_DIRECT === "true";
}

/**
 * Base URL of the Go API, for direct-mode app-logs. Empty string when unset.
 *
 * Read straight from process.env rather than importing MAIN_API_BASE_URL from
 * lib/config/env, deliberately. That module console.errors at import time when
 * the API base URLs are missing, and this file is on the every-page path via
 * instrumentation-client -> app_logs.client. Importing it would turn a config
 * mistake into console noise for every visitor on every page load, for no
 * benefit — this is the only value the logging path needs from it.
 *
 * The duplicated variable name is the cost of that. Keep it in step with
 * MAIN_API_BASE_URL in lib/config/env.ts if the Go API's env var is ever
 * renamed.
 */
export function getAppLogDirectBaseUrl() {
  return process.env.NEXT_PUBLIC_GOLANG_API_BASE_URL ?? "";
}
